/**
 * Landing page layout — section order and savings-plan slide order.
 *
 * Stored in `settings/app.landingLayout` so an admin can rearrange the public
 * home page without a deploy.
 */

import type { LandingLayoutConfig, LandingSectionId, SavingsPlanId } from '../types/firestore'
import { SAVINGS_PLAN_IDS } from './savingsPlans'

export const LANDING_SECTION_IDS: LandingSectionId[] = [
  'savingsPlans',
  'brandBanner',
  'scripture',
  'noInterest',
  'tagline',
  'howItWorks',
  'applyCtas',
  'fundImpact',
  'logo',
]

export const DEFAULT_LANDING_SECTION_ORDER: LandingSectionId[] = [...LANDING_SECTION_IDS]

export const DEFAULT_SAVINGS_PLAN_ORDER: SavingsPlanId[] = [...SAVINGS_PLAN_IDS]

export function normalizeSectionOrder(order: LandingSectionId[] | undefined): LandingSectionId[] {
  const seen = new Set<LandingSectionId>()
  const result: LandingSectionId[] = []

  for (const id of order ?? []) {
    if (LANDING_SECTION_IDS.includes(id) && !seen.has(id)) {
      result.push(id)
      seen.add(id)
    }
  }

  for (const id of LANDING_SECTION_IDS) {
    if (!seen.has(id)) result.push(id)
  }

  return result
}

export function normalizeSavingsPlanOrder(order: SavingsPlanId[] | undefined): SavingsPlanId[] {
  const seen = new Set<SavingsPlanId>()
  const result: SavingsPlanId[] = []

  for (const id of order ?? []) {
    if (SAVINGS_PLAN_IDS.includes(id) && !seen.has(id)) {
      result.push(id)
      seen.add(id)
    }
  }

  for (const id of SAVINGS_PLAN_IDS) {
    if (!seen.has(id)) result.push(id)
  }

  return result
}

export function mergeLandingLayout(
  stored: Partial<LandingLayoutConfig> | undefined,
): Required<LandingLayoutConfig> {
  return {
    sectionOrder: normalizeSectionOrder(stored?.sectionOrder),
    savingsPlanOrder: normalizeSavingsPlanOrder(stored?.savingsPlanOrder),
  }
}

export function moveItem<T>(items: T[], index: number, direction: 'up' | 'down'): T[] {
  const target = direction === 'up' ? index - 1 : index + 1
  if (target < 0 || target >= items.length) return items
  const next = [...items]
  ;[next[index], next[target]] = [next[target]!, next[index]!]
  return next
}

export function isLandingSectionId(value: string): value is LandingSectionId {
  return LANDING_SECTION_IDS.includes(value as LandingSectionId)
}
